import { apiClient } from '@/services/api/api';
import {
  ClientGroup,
  Deal,
  DealListing,
  GroupMember,
  CreateGroupRequest,
  UpdateGroupRequest,
  CreateDealRequest,
  UpdateDealRequest,
  CreateListingRequest,
  UpdateListingRequest,
  CreateMemberRequest,
  UpdateMemberRequest,
} from '@/types/clientGroups';
import { logger } from '@/utils/logger/loggerInstance';

// ─── Queries ──────────────────────────────────────────────────────────────────

const normalizeLegacyPipelineStatuses = (data: {
  groups: ClientGroup[];
}): { groups: ClientGroup[] } => ({
  groups: data.groups.map((group) => ({
    ...group,
    deals: group.deals.map((deal) => ({
      ...deal,
      status: deal.status === 'Escrow' ? 'Pending' : deal.status,
      listings: deal.listings?.map((listing) => ({
        ...listing,
        status: listing.status === 'Escrow' ? 'Pending' : listing.status,
      })),
    })),
  })),
});

/**
 * Get current advisor's client groups with nested deals, listings, and members.
 * Admins may pass an optional advisorId to view another advisor's groups.
 *
 * The admin "all advisors" case (no advisorId, caller is Admin/SuperAdmin) is
 * now server-paginated (see #1268) — pass `cursor` (from a prior response's
 * `nextCursor`) to fetch the next page. useClientGroups pages through it
 * automatically in the background so its many existing consumers keep
 * seeing one flat array, same as before pagination existed — see
 * useAdminProperties for the precedent this mirrors.
 */
export const getMyClientGroups = async (
  advisorId?: string,
  cursor?: string,
): Promise<{ groups: ClientGroup[]; nextCursor?: string }> => {
  const params = new URLSearchParams();
  if (advisorId) params.set('advisorId', advisorId);
  if (cursor) params.set('cursor', cursor);
  const query = params.toString();
  const url = query ? `/client-groups/my?${query}` : '/client-groups/my';

  logger.debug(`GET ${url}`);
  const response = await apiClient.get<{ groups: ClientGroup[]; nextCursor?: string }>(url);

  return { ...normalizeLegacyPipelineStatuses(response.data), nextCursor: response.data.nextCursor };
};

/**
 * Paginated + searched admin "all advisors" client groups list (see #1268).
 * Separate from getMyClientGroups so its many other consumers (which expect
 * the full flattened list) are untouched — this is for the admin all-groups
 * view specifically, which now pages through results instead of fetching
 * every group + its nested deals/members/properties in one call.
 */
export const getMyClientGroupsPaginated = async (params?: {
  limit?: number;
  cursor?: string;
  search?: string;
}): Promise<{ groups: ClientGroup[]; nextCursor?: string }> => {
  const url = '/client-groups/my';

  logger.debug(`GET ${url}`);
  const response = await apiClient.get<{ groups: ClientGroup[]; nextCursor?: string }>(url, {
    params: { limit: params?.limit, cursor: params?.cursor, search: params?.search || undefined },
  });

  const normalized = normalizeLegacyPipelineStatuses(response.data);
  return { ...normalized, nextCursor: response.data.nextCursor };
};

/**
 * Get all client groups the current buyer is a member of.
 * Only for non-advisor/non-admin users.
 */
export const getMyGroupMemberships = async (): Promise<{
  groups: ClientGroup[];
  nextCursor?: string;
}> => {
  const url = '/client-groups/my-memberships';

  logger.debug(`GET ${url}`);
  const response = await apiClient.get<{ groups: ClientGroup[] }>(url);

  // Not paginated — a buyer's own memberships are naturally small. nextCursor
  // is always undefined here so useClientGroups' auto-fetch stops after one page.
  return normalizeLegacyPipelineStatuses(response.data);
};

// ─── Inbox state ──────────────────────────────────────────────────────────────

export const setGroupInboxState = async (
  groupId: string,
  isInboxArchived: boolean,
): Promise<void> => {
  const url = `/client-groups/${encodeURIComponent(groupId)}/chat/inbox-state`;
  logger.debug(`PUT ${url}`);
  await apiClient.put(url, { isInboxArchived });
};

// ─── Group-property associations ───────────────────────────────────────────────

export const addPropertyToGroup = async (
  groupId: string,
  propertyId: string,
): Promise<{ message: string }> => {
  const url = `/client-groups/${encodeURIComponent(groupId)}/properties/${encodeURIComponent(
    propertyId,
  )}`;

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<{ message: string }>(url);

  return response.data;
};

export const removePropertyFromGroup = async (
  groupId: string,
  propertyId: string,
): Promise<{ message: string }> => {
  const url = `/client-groups/${encodeURIComponent(groupId)}/properties/${encodeURIComponent(
    propertyId,
  )}`;

  logger.debug(`DELETE ${url}`);
  const response = await apiClient.delete<{ message: string }>(url);

  return response.data;
};

// ─── Group mutations ───────────────────────────────────────────────────────────

export const createClientGroup = async (
  data: CreateGroupRequest,
): Promise<{ group: ClientGroup }> => {
  const url = '/client-groups';

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<{ group: ClientGroup }>(url, data);

  return response.data;
};

export const updateClientGroup = async (
  id: string,
  data: UpdateGroupRequest,
): Promise<{ group: ClientGroup }> => {
  const url = `/client-groups/${encodeURIComponent(id)}`;

  logger.debug(`PUT ${url}`);
  const response = await apiClient.put<{ group: ClientGroup }>(url, data);

  return response.data;
};

export const deleteClientGroup = async (id: string): Promise<{ message: string }> => {
  const url = `/client-groups/${encodeURIComponent(id)}`;

  logger.debug(`DELETE ${url}`);
  const response = await apiClient.delete<{ message: string }>(url);

  return response.data;
};

// ─── Deal mutations ────────────────────────────────────────────────────────────

export const createDeal = async (
  groupId: string,
  data: CreateDealRequest,
): Promise<{ deal: Deal }> => {
  const url = `/client-groups/${encodeURIComponent(groupId)}/deals`;

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<{ deal: Deal }>(url, data);

  return response.data;
};

export const updateDeal = async (
  dealId: string,
  data: UpdateDealRequest,
): Promise<{ deal: Deal }> => {
  const url = `/deals/${encodeURIComponent(dealId)}`;

  logger.debug(`PUT ${url}`);
  const response = await apiClient.put<{ deal: Deal }>(url, data);

  return response.data;
};

export const deleteDeal = async (dealId: string): Promise<{ message: string }> => {
  const url = `/deals/${encodeURIComponent(dealId)}`;

  logger.debug(`DELETE ${url}`);
  const response = await apiClient.delete<{ message: string }>(url);

  return response.data;
};

export const certifyDeal = async (dealId: string, initials: string): Promise<{ deal: Deal }> => {
  const url = `/deals/${encodeURIComponent(dealId)}/certify`;

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<{ deal: Deal }>(url, { initials });

  return response.data;
};

// ─── Listing mutations ─────────────────────────────────────────────────────────

export const createListing = async (
  dealId: string,
  data: CreateListingRequest,
): Promise<{ listing: DealListing }> => {
  const url = `/deals/${encodeURIComponent(dealId)}/listings`;

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<{ listing: DealListing }>(url, data);

  return response.data;
};

export const updateListing = async (
  listingId: string,
  data: UpdateListingRequest,
): Promise<{ listing: DealListing }> => {
  const url = `/listings/${encodeURIComponent(listingId)}`;

  logger.debug(`PUT ${url}`);
  const response = await apiClient.put<{ listing: DealListing }>(url, data);

  return response.data;
};

export const deleteListing = async (listingId: string): Promise<{ message: string }> => {
  const url = `/listings/${encodeURIComponent(listingId)}`;

  logger.debug(`DELETE ${url}`);
  const response = await apiClient.delete<{ message: string }>(url);

  return response.data;
};

export const certifyListing = async (
  listingId: string,
  initials: string,
): Promise<{ listing: DealListing }> => {
  const url = `/listings/${encodeURIComponent(listingId)}/certify`;

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<{ listing: DealListing }>(url, { initials });

  return response.data;
};

// ─── Member mutations ──────────────────────────────────────────────────────────

export const createMember = async (
  groupId: string,
  data: CreateMemberRequest,
): Promise<{ member: GroupMember }> => {
  const url = `/client-groups/${encodeURIComponent(groupId)}/members`;

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<{ member: GroupMember }>(url, data);

  return response.data;
};

export const updateMember = async (
  memberId: string,
  data: UpdateMemberRequest,
): Promise<{ member: GroupMember }> => {
  const url = `/members/${encodeURIComponent(memberId)}`;

  logger.debug(`PUT ${url}`);
  const response = await apiClient.put<{ member: GroupMember }>(url, data);

  return response.data;
};

export const deleteMember = async (memberId: string): Promise<{ message: string }> => {
  const url = `/members/${encodeURIComponent(memberId)}`;

  logger.debug(`DELETE ${url}`);
  const response = await apiClient.delete<{ message: string }>(url);

  return response.data;
};
